import React from 'react';
import { useParams } from "react-router";
import "./Blog.css";
import { getClientHost, getServerHost } from './Utils';


// Component to display an individual blog post
const BlogPostCard = ({ post, showEdit, onEditClick }) => {
  const imgLinkPre = "https://" + window.location.hostname + ":3001/photo?photo=";
  const imgLinkPost = "&width=" + 1024;
  const videoLinkPre = "https://" + window.location.hostname + ":3001/video?video=";
  return (
    <div className="blog-post-card">
      <div className="blog-post-title">
        <h2 className="post-card-title">
          {post.title}
        </h2>
        {showEdit &&
          <button
            className="edit-toggle-button"
            onClick={() => onEditClick(post)}
          >
            Edit Blog Post
          </button>
        }
      </div>
      <p className="post-card-date">
        Published on: <span className="post-card-date-value">{post.date}</span>
      </p>
      <div className="post-content-prose">
        {post.content.map((block, index) => (
          <React.Fragment key={index}>
            {block.type === 'text' && (
              <div className="post-content-text">
                {block.value}
              </div>
            )}
            {block.type === 'image' && (
              <div className="media-block-container">
                <div className="post-content-image-wrapper">
                  <a href={imgLinkPre + block.href}>
                    <img
                      src={imgLinkPre + block.href + imgLinkPost}
                      alt={block.alt}
                      className="post-content-image"
                      onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/600x400/CCCCCC/000000?text=Image+Not+Found'; }}
                    />
                  </a>
                </div>
                {block.alt && <p className="caption">{block.alt}</p>}
                {block.caption && <p className="caption">{block.caption}</p>/* Display image caption */}
              </div>
            )}
            {block.type === 'video' && (
              <div className="media-block-container">
                <div className="post-content-video-wrapper"> {/* New wrapper for video and its caption */}
                  <iframe
                    src={videoLinkPre + block.href}
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="post-content-video"
                  ></iframe>
                </div>
                {block.caption && <p className="caption">{block.caption}</p>/* Display video caption */}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// Component for the Blog Recent Posts
const BlogRecentPosts = ({ recents }) => {
  return (
    <aside className="blog-sidebar">
      <h3 className="sidebar-title">
        Recent Posts
      </h3>
      <nav>
        <ul className="sidebar-nav-list">
          {/* Display all posts in sidebar for navigation */}
          {recents && recents.map(post => (
            <li key={post.id}>
              <button
                onClick={() => window.location.href = getClientHost() + "blog/id/" + post.id}
                className="sidebar-button"
              >
                <span className="sidebar-button-title">{post.title}</span>
                <span className="sidebar-button-date">{post.date}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
};

// Component for the Blog Archived Posts
const BlogArchive = ({ postContent }) => {
  let pages = [];
  if (postContent && postContent.count && postContent.postsPerPage) {
    for (let i = 0; i < postContent.count; i += postContent.postsPerPage) {
      pages.push(i / postContent.postsPerPage);
    }
  }
  return (
    <div className="older-posts-archive">
      <h3 className="older-posts-archive-title">Archive Pages</h3>
      <ul className="older-posts-list">
        {pages.map((page) => (
          <li key={page} className="older-posts-list-item">
            <button
              onClick={() => window.location.href = getClientHost() + "blog/page/" + page}
              className="older-post-link-button"
            >
              {page}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Component for the Add New Post Form
const AddPostForm = ({
  showAddPostForm, setShowAddPostForm,
  editingPost,
  newPostTitle, setNewPostTitle,
  newPostContentBlocks, setNewPostContentBlocks,
  handleContentBlockChange, handleAddTextBlock,
  handleAddImageBlock, handleAddVideoBlock,
  handleRemoveBlock, handleNewPostSubmit
}) => {
  const imgLinkPre = "https://" + window.location.hostname + ":3001/photo?photo=";
  return (
    <section className="add-post-form-section">
      <h2 className="form-title">Create a New Blog Post</h2>
      <form onSubmit={handleNewPostSubmit}>
        <div className="form-group">
          <label htmlFor="post-title" className="form-label">Blog Post Title:</label>
          <input
            type="text"
            id="post-title"
            className="form-input"
            value={newPostTitle}
            onChange={(e) => {
              setNewPostTitle(e.target.value)
            }}
            required
          />
        </div>

        {/* Dynamic Content Blocks */}
        <div className="form-group">
          <label className="form-label">Content:</label>
          {newPostContentBlocks.map((block, index) => (
            <div key={index} className="content-block-container">
              {/* Remove button for each block */}
              <button
                type="button"
                onClick={() => handleRemoveBlock(index)}
                className="remove-block-button"
                title="Remove this block"
              >
                &times; {/* HTML entity for multiplication sign, looks like a cross */}
              </button>

              {block.type === 'text' && (
                <textarea
                  className="form-textarea"
                  placeholder="Start typing your blog post content here..."
                  value={block.value}
                  onChange={(e) => handleContentBlockChange(index, 'value', e.target.value)}
                ></textarea>
              )}
              {block.type === 'image' && (
                <div>
                  {block.value && <img src={block.value} alt={block.alt} className="post-content-image mb-2" />}
                  {!block.value && block.src &&
                    <img src={imgLinkPre + block.src} alt={block.alt} className="post-content-image mb-2" />
                  }
                  <p className="text-gray-600 text-sm mb-2">Image preview (select file below)</p>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-input"
                    onChange={(e) => handleContentBlockChange(index, 'file', e.target.files[0])} // Pass the file directly
                  />
                  <input
                    type="text"
                    className="form-input mt-2"
                    placeholder="Image caption (optional)"
                    value={block.caption}
                    onChange={(e) => handleContentBlockChange(index, 'caption', e.target.value)}
                  />
                </div>
              )}
              {block.type === 'video' && (
                <div>
                  {block.value && (
                    <div className="post-content-video-wrapper mb-2">
                      <iframe src={block.value} title={block.title} frameBorder="0" allowFullScreen className="post-content-video"></iframe>
                    </div>
                  )}
                  {!block.value && block.src && (
                    <div className="post-content-video-wrapper mb-2">
                      <iframe src={block.src} title={block.title} frameBorder="0" allowFullScreen className="post-content-video"></iframe>
                    </div>
                  )}
                  <p className="text-gray-600 text-sm mb-2">Video preview</p>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Video URL (e.g., https://kaylilin.com/video/abc.mp4)"
                    value={block.src} // Use block.value for video URL
                    onChange={(e) => handleContentBlockChange(index, 'videoUrl', e.target.value)} // New field for video URL
                  />
                  <input
                    type="text"
                    className="form-input mt-2"
                    placeholder="Video caption (optional)"
                    value={block.caption}
                    onChange={(e) => handleContentBlockChange(index, 'caption', e.target.value)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="add-content-buttons">
          <button type="button" onClick={handleAddTextBlock} className="add-content-button add-text-button">
            Add Text Block
          </button>
          <button type="button" onClick={handleAddImageBlock} className="add-content-button add-image-button">
            Add Image Block
          </button>
          <button type="button" onClick={handleAddVideoBlock} className="add-content-button add-video-button">
            Add Video Block
          </button>
        </div>

        <button type="submit" className="submit-button">
          {editingPost ? 'Edit Post' : 'Publish New Post'}
        </button>
      </form>
    </section>
  );
};

// Main App component for the Blog
const BlogPage = ({ showPost }) => {
  const [postContent, setPostContent] = React.useState(null);

  // State to control the visibility of the "Add New Post" form
  const [showAddPostForm, setShowAddPostForm] = React.useState(false);
  const [editingPost, setEditingPost] = React.useState(null);

  // States for the new post form
  const [newPostTitle, setNewPostTitle] = React.useState('');
  const [newPostContentBlocks, setNewPostContentBlocks] = React.useState([{ type: 'text', value: '' }]);

  // Functions to add content blocks (passed to AddPostForm)
  const handleAddTextBlock = () => {
    setNewPostContentBlocks([...newPostContentBlocks, { type: 'text', value: '' }]);
  };
  const handleAddImageBlock = () => {
    setNewPostContentBlocks([...newPostContentBlocks, { type: 'image', value: '', fileName: '', caption: '' }]);
  };
  const handleAddVideoBlock = () => {
    setNewPostContentBlocks([...newPostContentBlocks, { type: 'video', value: '', caption: '' }]);
  };
  const handleRemoveBlock = (indexToRemove) => {
    setNewPostContentBlocks(newPostContentBlocks.filter((_, index) => index !== indexToRemove));
  };

  const handleEditPost = (postToEdit) => {
    //console.log(JSON.stringify(postToEdit.content));
    setEditingPost(postToEdit);
    setNewPostTitle(postToEdit.title);
    setNewPostContentBlocks(JSON.parse(JSON.stringify(postToEdit.content || [])));
    setShowAddPostForm(true);
  };

  // Handle changes for any content block (passed to AddPostForm)
  const handleContentBlockChange = (index, field, value) => {
    const updatedBlocks = [...newPostContentBlocks];
    if (field === 'file') {
      const file = value;
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          updatedBlocks[index].fileName = file.name;
          updatedBlocks[index].value = reader.result;
          setNewPostContentBlocks(updatedBlocks);
        };
        reader.readAsDataURL(file);
      } else {
        updatedBlocks[index].value = '';
        setNewPostContentBlocks(updatedBlocks);
      }
    } else if (field === 'videoUrl') {
      let embedUrl = value;
      updatedBlocks[index].value = embedUrl;
      setNewPostContentBlocks(updatedBlocks);
    }
    else {
      updatedBlocks[index][field] = value;
      setNewPostContentBlocks(updatedBlocks);
    }
  };

  // Handle form submission for a new blog post (passed to AddPostForm)
  const handleNewPostSubmit = async (e) => { // Made function async
    e.preventDefault();
    
    console.log(editingPost ? "Submit edit " + editingPost.id : "Submit new");

    for (const block of newPostContentBlocks) {
      console.log("  Block type " + block.type);
    }

    let newPost = {
      // Note: id will typically be generated by the database
      title: newPostTitle,
      content: newPostContentBlocks.filter(block =>
        (block.type === 'text' && block.value.trim() !== '') ||
        (block.type === 'image' && block.value !== '') ||
        (block.type === 'video' && block.value !== '')
      ),
      slug: newPostTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-*|-*$/g, '')
    };
    
    // Editing a post requires the ID of the post
    if (editingPost)
      newPost.id = editingPost.id;
    
    console.log("JSON");
    console.log(JSON.stringify(newPost));

    try {
      const postQuery = editingPost ? "blog-update" : "blog-post";
      const response = await fetch(getServerHost() + postQuery, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify(newPost),
      });

      if (!response.ok) {
        // Handle HTTP errors
        const errorData = await response.json();
        throw new Error(`Server error: ${response.status} ${response.statusText} - ${errorData.message}`);
      }

      const result = await response.json();
      console.log('Post saved successfully:', result);

      // Close the form and refresh the page after successful submission
      setNewPostTitle('');
      setNewPostContentBlocks([{ type: 'text', value: '' }]);
      setShowAddPostForm(false);
      setEditingPost(null);
      window.location.reload(); // Refresh the entire page to fetch new data

    } catch (error) {
      console.error('Error saving post:', error);
      // You might want to display an error message to the user here
      alert('Failed to publish post. Please check the console for details and ensure the server is running.'); // Simple alert for demo
    }
  };

  let { page } = useParams();
  if (!page)
    page = 0;

  let { id } = useParams();

  React.useEffect(() => {
    let response = null;
    if (id !== undefined)
      response = fetch(getServerHost() + "blog?id=" + id);
    else
      response = fetch(getServerHost() + "blog?page=" + page)
    response
      .then((res) => res.json())
      .then((data) => {
        setPostContent(data);

      });
  }, [page, id]);

  let [recents, setRecents] = React.useState(null);
  React.useEffect(() => {
    fetch(getServerHost() + "blog/recents")
      .then((res) => res.json())
      .then((data) => {
        setRecents(data.recents);
      });
  }, []);

  return (
    <div className="blog-container">
      {/* Fixed Faint Background Gradient */}
      <div className="blog-background"></div>

      {/* Main Content Wrapper */}
      <div className="blog-content-wrapper">
        {/* Blog Header */}
        <header className="blog-header">
          <h1 className="blog-title">
            Kayli and Leia's Blog
          </h1>
          <p className="blog-subtitle">Adventures and Vacations!</p>
        </header>

        {/* Toggle Button for Add New Post Form */}
        {showPost &&
          <button
            className="add-post-toggle-button"
            onClick={() => setShowAddPostForm(!showAddPostForm)}
          >
            {showAddPostForm ? 'Hide Post Form' : 'Add New Blog Post'}
          </button>
        }

        {/* Add New Post Form Section */}
        {showAddPostForm && (
          <AddPostForm
            showAddPostForm={showAddPostForm}
            setShowAddPostForm={setShowAddPostForm}
            editingPost={editingPost}
            newPostTitle={newPostTitle}
            setNewPostTitle={setNewPostTitle}
            newPostContentBlocks={newPostContentBlocks}
            setNewPostContentBlocks={setNewPostContentBlocks}
            handleContentBlockChange={handleContentBlockChange}
            handleAddTextBlock={handleAddTextBlock}
            handleAddImageBlock={handleAddImageBlock}
            handleAddVideoBlock={handleAddVideoBlock}
            handleRemoveBlock={handleRemoveBlock}
            handleNewPostSubmit={handleNewPostSubmit}
          />
        )}

        {/* Main Blog Content Section */}
        <div className="blog-main-layout">
          {/* Main Blog Post Section */}
          <main className="blog-post-section">
            {/* Display up to 5 latest posts */}
            {postContent && postContent.posts && postContent.posts.map((post) => (
              <BlogPostCard key={post.id} post={post} showEdit={showPost} onEditClick={handleEditPost}/>
            ))}
            {postContent && postContent.posts && postContent.posts.length === 0 && (
              <p className="no-post-selected-message">No posts to display. Add a new post!</p>
            )}
            {/* Archive for accessing older Posts */}
            <BlogArchive
              postContent={postContent}
            />
          </main>

          <div className="blog-side-layout">
            {/* Sidebar for Older Posts */}
            <BlogRecentPosts
              recents={recents}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
